// src/pages/RetirementCalculator.tsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";

// ---------- helpers ----------
const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const parseNum = (v: string) => {
  const x = Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(x) ? x : 0;
};

// end-of-period annuity FV
function fvOfAnnuity(pmtMonthly: number, rMonthly: number, nMonths: number) {
  if (nMonths <= 0) return 0;
  if (Math.abs(rMonthly) < 1e-9) return pmtMonthly * nMonths;
  return pmtMonthly * ((Math.pow(1 + rMonthly, nMonths) - 1) / rMonthly);
}

// single sum FV
function fvLump(sum: number, rMonthly: number, nMonths: number) {
  if (nMonths <= 0) return sum;
  return sum * Math.pow(1 + rMonthly, nMonths);
}

// ---------- page ----------
export default function RetirementCalculator() {
  // Inputs
  const [age, setAge] = useState(29);
  const [retireAge, setRetireAge] = useState(65);
  const [lifeExp, setLifeExp] = useState(90);

  const [currentSavings, setCurrentSavings] = useState(30000);
  const [contribMonthly, setContribMonthly] = useState(400);

  const [salary, setSalary] = useState(90000);
  const [employerMatchPct, setEmployerMatchPct] = useState<number | "">(undefined as any);
  const [matchUpToPct, setMatchUpToPct] = useState<number | "">(undefined as any);

  const [desiredAnnual, setDesiredAnnual] = useState(60000);
  const [socialSecurityAnnual, setSSA] = useState(0);

  const [nominalReturnPct, setNominalReturnPct] = useState(7); // nominal
  const [inflationPct, setInflationPct] = useState(2.5);
  const [feeDragPct, setFeeDragPct] = useState(0.2);

  const swr = 0.04; // 4% real “safe withdrawal” rate

  const calc = useMemo(() => {
    const yearsToRetire = clamp(retireAge - age, 0, 80);
    const months = Math.round(yearsToRetire * 12);

    const rNom = nominalReturnPct / 100;
    const inf = inflationPct / 100;
    const fee = feeDragPct / 100;

    const rRealAnnual = (1 + rNom) / ((1 + inf) * (1 + fee)) - 1;
    const rMonthly = Math.pow(1 + rRealAnnual, 1 / 12) - 1;

    const employeeRate = salary > 0 ? (contribMonthly * 12) / salary : 0;
    const cap = (Number(matchUpToPct) || 0) / 100;
    const matchRate = (Number(employerMatchPct) || 0) / 100;

    const matchedRate = Math.min(employeeRate, cap);
    const employerMatchAnnual = salary * matchedRate * matchRate;
    const employerMatchMonthly = employerMatchAnnual / 12;

    const fvCurrent = fvLump(currentSavings, rMonthly, months);
    const fvYou = fvOfAnnuity(contribMonthly, rMonthly, months);
    const fvMatch = fvOfAnnuity(employerMatchMonthly, rMonthly, months);

    const totalAtRetirement = fvCurrent + fvYou + fvMatch;

    const sustainableAnnual = totalAtRetirement * swr;
    const desiredAfterSS = Math.max(0, desiredAnnual - socialSecurityAnnual);
    const shortfall = Math.max(0, desiredAfterSS - sustainableAnnual);

    const fvTarget = desiredAfterSS / swr;
    const annuityFactor =
      months > 0
        ? Math.abs(rMonthly) < 1e-9
          ? months
          : (Math.pow(1 + rMonthly, months) - 1) / rMonthly
        : 0;
    const fvExisting = totalAtRetirement;
    const extraMonthlyNeeded = annuityFactor > 0 ? Math.max(0, (fvTarget - fvExisting) / annuityFactor) : 0;

    return {
      months,
      yearsToRetire,
      rRealAnnual,
      rMonthly,
      employerMatchAnnual,
      employerMatchMonthly,
      fvCurrent,
      fvYou,
      fvMatch,
      totalAtRetirement,
      sustainableAnnual,
      desiredAfterSS,
      shortfall,
      extraMonthlyNeeded,
    };
  }, [
    age,
    retireAge,
    currentSavings,
    contribMonthly,
    salary,
    employerMatchPct,
    matchUpToPct,
    desiredAnnual,
    socialSecurityAnnual,
    nominalReturnPct,
    inflationPct,
    feeDragPct,
  ]);

  const canonical = "https://free1099calc.com/retirement";

  return (
    <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">
      <Helmet>
        <title>Free Retirement Calculator - Plan with Real Inflation-Adjusted Returns</title>
        <meta
          name="description"
          content="Free retirement planning calculator with inflation-adjusted returns. Calculate savings growth, employer match, and sustainable withdrawals using the 4% rule. Ideal for freelancers and 1099 workers."
        />
        <meta name="keywords" content="retirement calculator, inflation adjusted returns, 401k calculator, IRA calculator, retirement planning, safe withdrawal rate, 4% rule, employer match calculator, freelancer retirement" />
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Retirement Calculator with Real Returns | Free1099Calc" />
        <meta property="og:description" content="Plan retirement in today's dollars with inflation-adjusted returns, employer match calculations, and safe withdrawal estimates." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/retirement" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Free Retirement Calculator - Real Returns" />
        <meta name="twitter:description" content="Calculate retirement savings with inflation-adjusted returns and safe withdrawal rates." />
      </Helmet>

      {/* Hero */}
      <section className="mb-10 rounded-3xl border-2 border-gray-200 bg-gradient-to-br from-green-600 via-emerald-700 to-teal-900 p-10 md:p-14 shadow-2xl text-center">
        <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-5 py-2 mb-6">
          <svg className="w-5 h-5 text-white mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
          </svg>
          <span className="text-sm font-bold text-white">PLAN FOR THE FUTURE</span>
        </div>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-6 leading-tight">Retirement Calculator</h1>
        <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed">
          Plan in today's dollars. We compound your savings at a <strong className="text-white">real</strong> rate of return (net of
          inflation & fees) and estimate a sustainable withdrawal in retirement.
        </p>
      </section>

      {/* Expense Tracker Banner */}
      <div className="mb-8">
        <ExpenseTrackerBanner compact />
      </div>

      {/* Main Input: Age, Retirement, Life expectancy */}
      <section className="mb-8 rounded-3xl border-2 border-green-200 bg-white p-8 md:p-10 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-200 rounded-xl">
            <svg className="w-7 h-7 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Basic Timeline</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <NumberField label="Current Age" value={age} onChange={setAge} />
          <NumberField label="Retirement Age" value={retireAge} onChange={setRetireAge} />
          <NumberField label="Life Expectancy" value={lifeExp} onChange={setLifeExp} />
        </div>
      </section>

      {/* Inputs */}
      <section className="rounded-3xl border-2 border-blue-200 bg-white p-8 md:p-10 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl">
            <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Your Inputs</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <NumberField label="Current Age" value={age} onChange={setAge} />
          <NumberField label="Retirement Age" value={retireAge} onChange={setRetireAge} />
          <NumberField label="Life Expectancy" value={lifeExp} onChange={setLifeExp} />

          <CurrencyField label="Current Savings" value={currentSavings} onChange={setCurrentSavings} />
          <CurrencyField label="Monthly Contribution (you)" value={contribMonthly} onChange={setContribMonthly} />
          <CurrencyField label="Desired Annual Income (today’s $)" value={desiredAnnual} onChange={setDesiredAnnual} />

          <CurrencyField label="Annual Salary (for employer match)" value={salary} onChange={setSalary} />
          <PercentField placeholder="e.g. 3" label="Employer Match %" value={employerMatchPct} onChange={setEmployerMatchPct} />
          <PercentField placeholder="e.g. 3" label="Match Up To % of Salary" value={matchUpToPct} onChange={setMatchUpToPct} />

          <CurrencyField label="Social Security (annual, optional)" value={socialSecurityAnnual} onChange={setSSA} />

          <PercentField label="Expected Return (nominal %)" value={nominalReturnPct} onChange={setNominalReturnPct} />
          <PercentField label="Inflation (%)" value={inflationPct} onChange={setInflationPct} />
          <PercentField label="Fee Drag (%)" value={feeDragPct} onChange={setFeeDragPct} />
        </div>
      </section>

      {/* Results */}
      <section className="grid md:grid-cols-2 gap-8 mt-10">
        <div className="rounded-3xl border-2 border-green-200 bg-white overflow-hidden shadow-xl">
          <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Projection at Retirement</h2>
            </div>
            <p className="text-sm text-gray-600">
              {calc.yearsToRetire} years, real return ≈ {(calc.rRealAnnual * 100).toFixed(2)}% / yr (in today's dollars)
            </p>
          </div>
          <ul>
            <Row label="From current savings" value={fmt(calc.fvCurrent)} />
            <Row alt label="From your contributions" value={fmt(calc.fvYou)} />
            <Row label="From employer match" value={fmt(calc.fvMatch)} />
            <Row alt bold label="Total at retirement" value={fmt(calc.totalAtRetirement)} />
          </ul>
        </div>

        <div className="rounded-3xl border-2 border-blue-200 bg-white overflow-hidden shadow-xl">
          <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Income & Savings Target</h2>
            </div>
            <p className="text-sm text-gray-600">Based on 4% safe withdrawal rate (in today's dollars)</p>
          </div>
          <ul>
            <Row label="Sustainable annual withdrawals" value={fmt(calc.sustainableAnnual)} />
            <Row alt label="Desired annual income (after Social Security)" value={fmt(calc.desiredAfterSS)} />
            <Row label="Estimated shortfall" value={fmt(calc.shortfall)} />
            <Row alt bold label="Required monthly savings (you)" value={fmt(calc.extraMonthlyNeeded)} />
          </ul>
          <div className="p-4 text-xs text-slate-600">
            Uses inflation-adjusted returns (net of fees). Sustainable withdrawals assume a constant-dollar{" "}
            <strong>~4%</strong> rule. Real dollars mean today’s purchasing power.
          </div>
        </div>
      </section>

      {/* How it works (matches site style) */}
      <section className="mt-10 rounded-3xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 p-8 md:p-10 shadow-xl">
        <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
            </svg>
          </div>
          How this retirement calculator works
        </h2>
        <p className="text-gray-700 text-lg leading-relaxed">
          We grow your current balance and future contributions using a <strong>real</strong> rate of return (expected
          nominal return minus inflation and fee drag). At retirement, we estimate a sustainable, inflation-adjusted
          withdrawal using a constant-dollar rule of thumb.
        </p>

        <h3 className="text-xl font-bold mt-8 mb-4 text-gray-900">Who is this for?</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 text-base">{" "}
          <li>Freelancers, consultants, and small business owners with 1099 income</li>
          <li>W-2 employees with access to employer retirement plans and match</li>
          <li>Anyone who wants a clear monthly savings target in today’s dollars</li>
        </ul>

        <h3 className="text-xl font-bold mt-8 mb-4 text-gray-900">Planning tips</h3>
        <ul className="list-disc pl-6 text-gray-700 space-y-2 text-base">
          <li>Lower fees raise your long-run real return—low-cost index funds help.</li>
          <li>Consider nudging your savings rate up with each raise or new contract.</li>
          <li>Revisit assumptions annually; markets, inflation, and fees change.</li>
          <li>Keep an emergency fund separate so you can stay invested during downturns.</li>
          <li>Max tax-advantaged accounts first (401(k), IRA, HSA) before taxable.</li>
        </ul>
      </section>

      {/* Educational Content */}
      <section className="mt-8 rounded-3xl border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-cyan-50 p-8 md:p-10 shadow-xl">
        <h2 className="text-3xl font-black mb-6 flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-lg">
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
            </svg>
          </div>
          Understanding Retirement Planning for Freelancers
        </h2>
        
        <div className="space-y-8 text-gray-700">
          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Why Freelancers Need Different Retirement Planning</h3>
            <p className="mb-4">
              Traditional employees often have access to employer-sponsored 401(k) plans with automatic contributions 
              and matching funds. As a <strong>1099 contractor or self-employed professional</strong>, you're responsible 
              for setting up and funding your own retirement accounts. This requires more discipline and planning, but 
              also offers more flexibility and control.
            </p>
            <p>
              Our calculator uses <strong>real (inflation-adjusted) returns</strong> to show you what your savings will 
              actually be worth in today's dollars. This is critical because $1 million in 30 years won't have the same 
              purchasing power as $1 million today.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Retirement Account Options for 1099 Workers</h3>
            <p className="mb-3">As a freelancer or independent contractor, you have several retirement account options:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Solo 401(k):</strong> Allows contributions as both employer and employee. You can contribute up to $23,000 as an employee ({new Date().getFullYear()}) plus up to 25% of your net self-employment income as employer contributions, with a total limit of $69,000.</li>
              <li><strong>SEP IRA:</strong> Simpler to set up than a Solo 401(k). You can contribute up to 25% of your net self-employment income, with a maximum of $69,000 ({new Date().getFullYear()}). Great for high earners with variable income.</li>
              <li><strong>Traditional or Roth IRA:</strong> Available to anyone with earned income. Contribution limit is $7,000 ($8,000 if age 50+). Roth IRA has income phase-out limits.</li>
              <li><strong>Simple IRA:</strong> Good option if you have employees. Allows $16,000 in contributions ($19,500 if 50+) with required employer matching or contributions.</li>
            </ul>
            <p>
              Most self-employed individuals benefit most from a <strong>Solo 401(k)</strong> due to the high contribution 
              limits and flexibility to contribute as both employer and employee.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">The 4% Safe Withdrawal Rule</h3>
            <p className="mb-4">
              Our calculator uses the <strong>4% rule</strong> to estimate sustainable retirement withdrawals. This rule, 
              based on historical market data, suggests you can withdraw 4% of your portfolio annually (adjusted for 
              inflation) with a high probability of not running out of money over a 30-year retirement.
            </p>
            <p className="mb-4">
              For example, if you retire with $1 million in today's dollars, the 4% rule suggests you can safely withdraw 
              $40,000 per year. Combined with Social Security benefits, this forms your retirement income.
            </p>
            <p>
              <strong>Important:</strong> The 4% rule is a guideline, not a guarantee. Market conditions, retirement 
              length, and spending flexibility all affect sustainability. Conservative planners often use 3-3.5% instead.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Understanding Investment Returns and Fees</h3>
            <p className="mb-4">
              <strong>Nominal vs. Real Returns:</strong> If your investments earn 7% annually but inflation is 2.5%, 
              your "real" return is approximately 4.5%. Real returns show what your money can actually buy in the future. 
              This calculator focuses on real returns to give you accurate purchasing power estimates.
            </p>
            <p className="mb-4">
              <strong>Fee Impact:</strong> Even small fees compound dramatically over time. A 1% annual fee on a $500,000 
              portfolio costs $5,000 per year, and that money loses decades of potential compound growth. Low-cost index 
              funds typically charge 0.03-0.20%, while actively managed funds may charge 0.5-1.5% or more.
            </p>
            <p>
              <strong>Recommended:</strong> For most freelancers, a diversified portfolio of low-cost index funds 
              (S&P 500, total US stock market, international stocks, bonds) provides excellent long-term growth with 
              minimal fees.
            </p>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Action Steps for Freelancers</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Set up a retirement account now</strong> - Even if you can only contribute $100/month, starting early gives compound growth decades to work.</li>
              <li><strong>Automate contributions</strong> - Set up automatic monthly transfers to your retirement account so saving becomes effortless.</li>
              <li><strong>Increase savings with income</strong> - When you land a new client or raise your rates, commit to increasing retirement contributions by 20-50% of the income boost.</li>
              <li><strong>Take advantage of catch-up years</strong> - In high-income years, maximize contributions. In lean years, contribute what you can.</li>
              <li><strong>Build an emergency fund first</strong> - Keep 3-6 months of expenses in a savings account before aggressive retirement saving. This prevents early withdrawal penalties.</li>
              <li><strong>Consider a backdoor Roth IRA</strong> - If your income exceeds Roth IRA limits, you can contribute to a traditional IRA and convert to Roth.</li>
              <li><strong>Review annually</strong> - Check your progress, rebalance your portfolio, and adjust contributions based on income changes.</li>
            </ol>
          </div>

          <div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Related Tools for Freelancers</h3>
            <p className="text-gray-700 mb-6 text-base">
              Build a complete financial plan with these complementary calculators:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">1099 Tax Calculator</h4>
                <p className="text-sm text-gray-600">
                  Estimate your tax burden including self-employment tax. Know how much to set aside for quarterlies.
                </p>
              </Link>
              
              <Link to="/calculators/inflation" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">Inflation Calculator</h4>
                <p className="text-sm text-gray-600">
                  See why nominal returns mislead. Understand purchasing power erosion over decades.
                </p>
              </Link>
              
              <Link to="/trends" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">Job Market Trends</h4>
                <p className="text-sm text-gray-600">
                  Benchmark your rates against industry standards. Plan career moves strategically.
                </p>
              </Link>
              
              <Link to="/blog/external" className="block border-2 border-primary-200 rounded-3xl p-6 hover:shadow-xl hover:border-primary-300 transition-all bg-white">
                <h4 className="font-bold text-lg text-primary-700 mb-2">External Resources</h4>
                <p className="text-sm text-gray-600">
                  IRS, SSA, and BLS official links for retirement planning and Social Security estimates.
                </p>
              </Link>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-primary-600 p-4 rounded">
            <p className="text-sm text-slate-800">
              <strong>Disclaimer:</strong> This calculator provides estimates for educational purposes only. It does not 
              account for taxes on withdrawals, required minimum distributions (RMDs), or sequence-of-returns risk. 
              Always consult with a qualified financial advisor or tax professional before making retirement planning 
              decisions.
            </p>
          </div>
        </div>
      </section>

      <RecentPosts />
    </main>
  );
}

/* ------- small presentational bits ------- */
function Row({
  label,
  value,
  alt,
  bold,
}: {
  label: string;
  value: string;
  alt?: boolean;
  bold?: boolean;
}) {
  const rowCls = alt ? "bg-slate-50/60" : "bg-white";
  const valCls = bold ? "font-semibold" : "font-medium";
  return (
    <li className={`flex justify-between px-4 py-3 border-b ${rowCls}`}>
      <span>{label}</span>
      <span className={valCls}>{value}</span>
    </li>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const [raw, setRaw] = useState<string>(String(value));
  React.useEffect(() => {
    const v = String(value);
    if (v !== raw) setRaw(v);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        className="mt-1 w-full rounded border px-3 py-2"
        value={raw}
        onChange={(e) => {
          const next = e.target.value.replace(/[^0-9]/g, "").replace(/^0+(?=\d)/, "");
          setRaw(next);
          const n = Number(next);
          if (Number.isFinite(n)) onChange(n);
        }}
        onBlur={() => {
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : 0);
          setRaw(String(Number.isFinite(n) ? n : 0));
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function CurrencyField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="mt-1 w-full rounded border px-3 py-2"
        value={String(value)}
        onChange={(e) => onChange(parseNum(e.target.value))}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}

function PercentField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: number | "";
  onChange: (n: number | "") => void;
  placeholder?: string;
}) {
  const [raw, setRaw] = useState(value === "" ? "" : String(value));
  React.useEffect(() => {
    setRaw(value === "" ? "" : String(value));
  }, [value]);

  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type="text"
        inputMode="decimal"
        className="mt-1 w-full rounded border px-3 py-2"
        value={raw}
        placeholder={placeholder}
        onChange={(e) => {
          const v = e.target.value.replace(/[^0-9.]/g, "");
          setRaw(v);
          onChange(v === "" ? "" : Number(v));
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
    </label>
  );
}
